import {
	addDays,
	addMinutes,
	differenceInMinutes,
	format,
	isBefore,
	isValid,
	parse,
} from "date-fns";
import React, { useCallback } from "react";
import {
	BooleanInput,
	ReferenceInput,
	SelectInput,
	TextInput,
	maxLength,
	required,
	useTranslate,
} from "react-admin";
import { useForm, useFormState } from "react-final-form";

import { FormGroupGrid, GridForm, GridInput } from "../../../components/GridForm";
import { DateInput, TimeInput } from "../../../components/inputs";
import { NumberWithSuffixInput } from "../../../components/inputs/NumberWithSuffixInput";
import { OSFCUserSelectInput } from "../../../components/inputs/OSFCUserSelect";
import TerminalDatesInput from "../../../components/inputs/TerminalDatesInput";
import { RecurringTaskRecurringOptionsType } from "../../../entities/RecurringTaskRecurringOptionsType";
import { TaskService } from "../../../entities/TaskService";
import { ServiceInput, unlockedServiceChoices } from "../../tasks/forms/inputs/ServiceInput";
import { EndTimeSettings, RepeatOptions, RepeatType } from "../RecurringOptions";
import { addTimeToDate } from "../utils/timeOperations";

const requiredValidator = [required()];

export const RecurringTaskForm = (props) => {
	const form = useForm();
	const translate = useTranslate();
	const { values } = useFormState();
	const validateTimeFormat = useCallback(
		(value) => {
			if (!value) {
				return undefined;
			}
			const time = parse(value, "HH:mm", new Date());
			if (!isValid(time)) {
				return translate("osfc.messages.validation.invalidTimeFormat");
			}
			return undefined;
		},
		[translate]
	);

	const validateTime = useCallback(
		(value, allValues) => {
			const { start_time } = allValues;
			if (!value || !start_time) {
				return undefined;
			}
			const startTime = parse(start_time, "HH:mm", new Date());
			const endTime = parse(value, "HH:mm", new Date());

			if (!isValid(startTime) || !isValid(endTime)) {
				return undefined;
			}

			if (endTime.getTime() === startTime.getTime()) {
				return translate("osfc.messages.validation.endTimeEqualStart");
			}

			if (isBefore(endTime, startTime)) {
				endTime.setDate(endTime.getDate() + 1);
			}

			const duration = differenceInMinutes(endTime, startTime);
			if (duration < 5) {
				return translate("osfc.messages.validation.minDuration", { min: 5 });
			}

			return undefined;
		},
		[translate]
	);

	React.useEffect(() => {
		if (!values.repeat_every) {
			form.change("repeat_every", 1);
		}
		if (values.end_option === RecurringTaskRecurringOptionsType.after) {
			form.change(
				"end_date",
				addTimeToDate(
					values.start_date,
					values.repeat_times || 1,
					values.repeat_every || 1,
					values.type
				)
			);
		}
	}, [
		values.repeat_every,
		values.end_option,
		values.start_date,
		values.repeat_times,
		values.type,
		form,
	]);

	const handleTimeChange = useCallback(() => {
		const { start_time, end_time, start_date } = form.getState().values;
		if (!start_time || !end_time) return;
		const date1 = parse(start_time, "HH:mm", new Date());
		const date2 = parse(end_time, "HH:mm", new Date());
		if (isValid(date1) && isValid(date2)) {
			if (isBefore(date2, date1)) {
				date2.setDate(date2.getDate() + 1);
				form.change("end_date", format(addDays(new Date(start_date), 1), "yyyy-MM-dd"));
			}

			const newDuration = differenceInMinutes(date2, date1);
			if (newDuration >= 0) {
				form.change("estimate_duration", newDuration);
			}
		}
	}, [form]);

	const handleDurationChange = useCallback(
		(event) => {
			const newDuration = parseInt(event.target.value, 10);
			const { start_time } = form.getState().values;
			if (!start_time || isNaN(newDuration) || newDuration < 0) return;
			const startDate = parse(start_time, "HH:mm", new Date());
			if (isValid(startDate)) {
				const newEndDate = addMinutes(startDate, newDuration);
				const newEndTime = format(newEndDate, "HH:mm");
				form.change("end_time", newEndTime);
			}
		},
		[form]
	);

	const onRepeatTypeChange = (event) => {
		if (values.end_option === RecurringTaskRecurringOptionsType.after) {
			form.change(
				"end_date",
				addTimeToDate(
					values.start_date,
					values.repeat_times,
					values.repeat_every,
					event.target.value
				)
			);
		} else if (values.end_option === RecurringTaskRecurringOptionsType.on) {
			form.change("repeat_times", null);
		}
	};

	return (
		<GridForm {...props}>
			<FormGroupGrid>
				<GridInput xs={12} sm={3} component={TextInput} source="title" />

				<GridInput
					source="service"
					xs={12}
					sm={3}
					component={ServiceInput}
					choices={unlockedServiceChoices}
					validate={requiredValidator}
					defaultValue={TaskService.other}
				/>

				<GridInput xs={12} sm={3}>
					<ReferenceInput source="terminal_id" reference="terminal" validate={requiredValidator}>
						<SelectInput optionText="name" />
					</ReferenceInput>
				</GridInput>

				<GridInput xs={12} sm={1} component={BooleanInput} source="priority" />
			</FormGroupGrid>

			<FormGroupGrid>
				<GridInput xs={12} sm={3}>
					<TerminalDatesInput
						component={DateInput}
						source="start_date"
						validate={requiredValidator}
					/>
				</GridInput>
				<GridInput
					xs={12}
					sm={9}
					md={6}
					lg={3}
					component={RepeatType}
					values={values}
					form={form}
					onChange={onRepeatTypeChange}
				/>

				<RepeatOptions form={form} values={values} />
			</FormGroupGrid>
			<FormGroupGrid style={{ marginBottom: "15px" }}>
				<GridInput xs={12} sm={3}>
					<TerminalDatesInput
						component={TimeInput}
						source="start_time"
						validate={[...requiredValidator, validateTimeFormat]}
						onChange={handleTimeChange}
					/>
				</GridInput>
				<GridInput xs={12} sm={3}>
					<TerminalDatesInput
						component={TimeInput}
						source="end_time"
						validate={[...requiredValidator, validateTimeFormat, validateTime]}
						onChange={handleTimeChange}
					/>
				</GridInput>
				<GridInput
					xs={6}
					sm={6}
					md={3}
					component={NumberWithSuffixInput}
					source="estimate_duration"
					validate={[required()]}
					min={5}
					suffix="minutes"
					onChange={handleDurationChange}
				/>
			</FormGroupGrid>

			<FormGroupGrid title={translate("resources.recurring-tasks.fields.when_to_stop")}>
				<EndTimeSettings form={form} values={values} xs={12} md={12} />
			</FormGroupGrid>

			<FormGroupGrid>
				<GridInput source="assignee_1" xs={12} sm={6} component={OSFCUserSelectInput} />
				<GridInput source="assignee_2" xs={12} sm={6} component={OSFCUserSelectInput} />
			</FormGroupGrid>

			<FormGroupGrid>
				<GridInput
					sm={12}
					md={12}
					component={TextInput}
					source="description"
					multiline
					rows={6}
					validate={[maxLength(1024)]}
				/>
			</FormGroupGrid>
		</GridForm>
	);
};
